import { BarChart3, Target, Calendar, TrendingUp, Volume, Activity, Clock } from 'lucide-react'
import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'

// Base filter node schema structure
const createBaseFilterSchema = (
  type: string,
  displayName: string,
  icon: any,
  description?: string,
): Pick<UniversalNodeSchema, 'type' | 'displayName' | 'category' | 'icon' | 'description'> => ({
  type,
  displayName,
  category: 'filter' as const,
  icon,
  description,
})

// Daily Percent Change Node
export const dailyPercentChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'dailyPercentChange',
    'Daily % Change',
    Clock,
    'Daily percentage change over specified days',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'daysBack',
      displayName: 'Days Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 30,
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change %',
      type: 'number',
    },
  ],
}

// Session Percent Change Node
export const sessionPercentChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'sessionPercentChange',
    'Session % Change',
    Clock,
    'Session percentage change from market open',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [],
  outputs: [
    {
      id: 'value',
      displayName: 'Change %',
      type: 'number',
    },
  ],
}

// Weekly Percent Change Node
export const weeklyPercentChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'weeklyPercentChange',
    'Weekly % Change',
    Calendar,
    'Weekly percentage change over specified weeks',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'weeksBack',
      displayName: 'Weeks Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 52,
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change %',
      type: 'number',
    },
  ],
}

// Monthly Percent Change Node
export const monthlyPercentChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'monthlyPercentChange',
    'Monthly % Change',
    Calendar,
    'Monthly percentage change over specified months',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'monthsBack',
      displayName: 'Months Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 24,
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change %',
      type: 'number',
    },
  ],
}

// Yearly Percent Change Node
export const yearlyPercentChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'yearlyPercentChange',
    'Yearly % Change',
    Calendar,
    'Yearly percentage change over specified years',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'yearsBack',
      displayName: 'Years Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 10,
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change %',
      type: 'number',
    },
  ],
}

// Custom Period Percent Change Node
export const customPeriodPercentChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'customPeriodPercentChange',
    'Custom Period % Change',
    Clock,
    'Custom period percentage change',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 5,
      min: 1,
      max: 1000,
    },
    {
      name: 'timeframe',
      displayName: 'Timeframe',
      type: 'select',
      default: 'candles',
      options: [
        { label: 'Candles', value: 'candles' },
        { label: 'Minutes', value: 'minutes' },
        { label: 'Hours', value: 'hours' },
        { label: 'Days', value: 'days' },
      ],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change %',
      type: 'number',
    },
  ],
}

// Total Volume Node
export const totalVolumeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'totalVolume',
    'Total Volume',
    Volume,
    'Total volume over specified period',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 10,
      min: 1,
      max: 1000,
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Volume',
      type: 'number',
    },
  ],
}

// Session Volume Node
export const sessionVolumeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema('sessionVolume', 'Session Volume', Volume, 'Current session volume'),
  inputs: [
    {
      id: 'data',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [],
  outputs: [
    {
      id: 'value',
      displayName: 'Volume',
      type: 'number',
    },
  ],
}

// Custom Period Volume Node
export const customPeriodVolumeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'customPeriodVolume',
    'Custom Period Volume',
    Volume,
    'Custom period volume',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 5,
      min: 1,
      max: 1000,
    },
    {
      name: 'timeframe',
      displayName: 'Timeframe',
      type: 'select',
      default: 'candles',
      options: [
        { label: 'Candles', value: 'candles' },
        { label: 'Minutes', value: 'minutes' },
        { label: 'Hours', value: 'hours' },
        { label: 'Days', value: 'days' },
      ],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Volume',
      type: 'number',
    },
  ],
}

// Percent Change From Open Node
export const percentChangeFromOpenSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'percentChangeFromOpen',
    'Chg from Open',
    Target,
    'Percentage change from open price',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'outputType',
      displayName: 'Output',
      type: 'select',
      default: 'Change %',
      options: [
        { label: 'Change', value: 'Change' },
        { label: 'Change %', value: 'Change %' },
      ],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change',
      type: 'number',
    },
  ],
}

// Percent Change From Close Node
export const percentChangeFromCloseSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'percentChangeFromClose',
    'Chg from Close',
    Target,
    'Percentage change from close price',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'outputType',
      displayName: 'Output',
      type: 'select',
      default: 'Change %',
      options: [
        { label: 'Change', value: 'Change' },
        { label: 'Change %', value: 'Change %' },
      ],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change',
      type: 'number',
    },
  ],
}

// Percent Change From High Node
export const percentChangeFromHighSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'percentChangeFromHigh',
    'Chg from High',
    TrendingUp,
    'Percentage change from high price',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'outputType',
      displayName: 'Output',
      type: 'select',
      default: 'Change %',
      options: [
        { label: 'Change', value: 'Change' },
        { label: 'Change %', value: 'Change %' },
      ],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change',
      type: 'number',
    },
  ],
}

// Percent Change From Low Node
export const percentChangeFromLowSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'percentChangeFromLow',
    'Chg from Low',
    TrendingUp,
    'Percentage change from low price',
  ),
  inputs: [
    {
      id: 'data',
      displayName: 'Price',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
    },
  ],
  parameters: [
    {
      name: 'outputType',
      displayName: 'Output',
      type: 'select',
      default: 'Change %',
      options: [
        { label: 'Change', value: 'Change' },
        { label: 'Change %', value: 'Change %' },
      ],
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change',
      type: 'number',
    },
  ],
}

// Price Change Node
export const priceChangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'priceChange',
    'Price Change',
    Activity,
    'Change percent between current price and historical point',
  ),
  face: { shortName: 'Price Change' },
  category: 'utilities',
  hasEquation: true,
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
      fieldSelection: {
        options: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  parameters: [
    {
      name: 'timeframe',
      displayName: 'Timeframe',
      type: 'select',
      default: 'daily',
      options: [
        { label: 'Daily', value: 'daily' },
        { label: 'Session', value: 'session' },
        { label: 'X Bars', value: 'xbars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 14,
      min: 1,
      max: 100,
      dependsOn: {
        parameter: 'timeframe',
        values: ['xbars'],
      },
    },
    {
      name: 'daysBack',
      displayName: 'Days Back',
      type: 'int',
      default: 1,
      min: 1,
      max: 5,
      dependsOn: {
        parameter: 'timeframe',
        values: ['daily'],
      },
    },
    {
      name: 'reference',
      displayName: 'Reference',
      type: 'select',
      default: 'close',
      options: [
        { label: 'Open', value: 'open' },
        { label: 'High', value: 'high' },
        { label: 'Low', value: 'low' },
        { label: 'Close', value: 'close' },
      ],
      dependsOn: {
        parameter: 'timeframe',
        values: ['daily'],
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Change',
      type: 'number',
    },
  ],
}

// Position In Range Node
export const positionInRangeSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema(
    'positionInRange',
    'Position in Range',
    BarChart3,
    'Position within price range',
  ),
  face: { shortName: 'Position In Range' },
  category: 'utilities',
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
      fieldSelection: {
        options: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  parameters: [
    {
      name: 'timeframe',
      displayName: 'Timeframe',
      type: 'select',
      default: 'daily',
      options: [
        { label: 'Daily', value: 'daily' },
        { label: 'Session', value: 'session' },
        { label: 'X Bars', value: 'xbars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 20,
      min: 2,
      max: 200,
      dependsOn: {
        parameter: 'timeframe',
        values: ['daily', 'xbars'],
      },
    },
    {
      name: 'extendedHours',
      displayName: 'Extended Hours',
      type: 'boolean',
      default: false,
      dependsOn: {
        parameter: 'timeframe',
        values: ['session'],
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Position',
      type: 'number',
    },
  ],
}

// Volume Filter Node
export const volumeFilterSchema: UniversalNodeSchema = {
  ...createBaseFilterSchema('volumeFilter', 'Volume', Volume, 'Volume-based filtering'),
  face: { shortName: 'Volume Filter' },
  category: 'utilities',
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'Data',
      acceptedTypes: ['ohlcv'],
      acceptedSources: ['priceData', 'marketData', 'volumeData'],
      fieldSelection: {
        options: [{ value: 'volume', label: 'Volume' }],
        defaultField: 'volume',
      },
    },
  ],
  parameters: [
    {
      name: 'volumeType',
      displayName: 'Type',
      type: 'select',
      default: 'total',
      options: [
        { label: 'Total', value: 'total' },
        { label: 'Session', value: 'session' },
        { label: 'X Bars', value: 'xbars' },
      ],
    },
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 5,
      min: 1,
      max: 100,
      dependsOn: {
        parameter: 'volumeType',
        values: ['xbars'],
      },
    },
    {
      name: 'outputType',
      displayName: 'Output Type',
      type: 'select',
      default: 'total',
      options: [
        { label: 'Total', value: 'total' },
        { label: 'Ratio %', value: 'ratio' },
      ],
      dependsOn: {
        parameter: 'volumeType',
        values: ['xbars'],
      },
    },
  ],
  outputs: [
    {
      id: 'value',
      displayName: 'Volume',
      type: 'number',
    },
  ],
}

// Array of simplified filter schemas for registration (only 3 nodes)
export const filterSchemas: UniversalNodeSchema[] = [
  priceChangeSchema,
  positionInRangeSchema,
  volumeFilterSchema,
]

// Registration function
export function registerFilterSchemas() {
  // console.log('Registering Filter Node Schemas...');

  filterSchemas.forEach((schema) => {
    UniversalNodeRegistry.register(schema)
    // console.log(`Registered filter schema: ${schema.type}`);
  })

  // console.log(`Registered ${filterSchemas.length} Filter Node Schemas:`,
  //   'Position in Range, Price Change, Volume Filter, Time Signal'
  // );
}
